import {
  PageTheme,
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
} from '@backstage/theme';

const BRAND = '#FF345A';
const BRAND_DARK = '#C8173B';

const headerTheme = genPageTheme({
  colors: [BRAND, '#9C1F3C', '#1D1A1F'],
  shape: 'none',
});

const pageTheme: Record<string, PageTheme> = Object.fromEntries(
  ['home', 'tool', 'app', 'other', 'card'].map(id => [id, headerTheme]),
);

type Tokens = {
  line: string;
  muted: string;
  text: string;
  surface: string;
};

const HERO_GRADIENT =
  'linear-gradient(110deg, #FF345A 0%, #9C1F3C 58%, #1D1A1F 100%)';

const components = ({ line, muted, text, surface }: Tokens) => ({
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundImage: HERO_GRADIENT,
        padding: '22px 32px 20px',
        boxShadow: 'none',
        minHeight: 0,
        alignItems: 'flex-end',
      },
      rightItemsBox: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 24,
        flexWrap: 'wrap' as const,
      },
      title: {
        fontSize: 30,
        fontWeight: 700,
        letterSpacing: '-0.3px',
        lineHeight: 1.2,
      },
      subtitle: {
        fontSize: 14,
        opacity: 0.9,
        marginTop: 4,
      },
      type: {
        fontSize: 11,
        letterSpacing: '.1em',
        textTransform: 'uppercase' as const,
        opacity: 0.85,
      },
    },
  },
  BackstageHeaderLabel: {
    styleOverrides: {
      root: { margin: 0 },
      label: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase' as const,
        opacity: 0.8,
        marginBottom: 4,
      },
      value: {
        fontSize: 14,
        fontWeight: 500,
        color: '#FFFFFF',
      },
    },
  },
  BackstageHeaderTabs: {
    styleOverrides: {
      tabsWrapper: {
        backgroundColor: surface,
        borderBottom: `1px solid ${line}`,
        paddingLeft: 24,
      },
      defaultTab: {
        textTransform: 'none' as const,
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: 0,
        padding: '14px 14px 11px',
        minWidth: 0,
        color: muted,
      },
      selected: {
        color: text,
      },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      root: { height: 40 },
      label: { fontWeight: 500, fontSize: 14 },
      iconContainer: { '& svg': { fontSize: 18 } },
      selected: {
        '&$root': { backgroundColor: 'rgba(255,255,255,0.06)' },
      },
    },
  },
  BackstageContent: {
    styleOverrides: {
      root: {
        padding: '24px 32px 48px',
      },
    },
  },
  BackstageInfoCard: {
    styleOverrides: {
      header: {
        padding: '14px 18px',
      },
      headerTitle: {
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.4,
      },
      headerSubheader: {
        paddingTop: 2,
        fontSize: 12,
        color: muted,
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      title: { fontSize: 16, fontWeight: 700 },
      subheader: { fontSize: 12 },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: '16px 18px' },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 3, backgroundColor: BRAND },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none' as const, fontWeight: 500, minWidth: 0 },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none' as const, fontWeight: 500, borderRadius: 7 },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        border: `1px solid ${line}`,
        borderRadius: 10,
        boxShadow: '0 1px 2px rgba(29,26,31,.06), 0 2px 8px rgba(29,26,31,.04)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: 10 },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { fontWeight: 500, fontSize: 12, borderRadius: 999, height: 22 },
      sizeSmall: { height: 20 },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '.07em',
        textTransform: 'uppercase' as const,
        color: muted,
        padding: '10px 18px',
        borderBottom: `1px solid ${line}`,
      },
      body: {
        padding: '10px 18px',
        borderBottom: `1px solid ${line}`,
      },
    },
  },
});

export const koviTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: { main: BRAND, dark: BRAND_DARK, contrastText: '#ffffff' },
      secondary: { main: '#565a6e' },
      error: { main: '#C62D3A' },
      warning: { main: '#B26A00' },
      info: { main: '#2B6CB0' },
      success: { main: '#1E8E4E' },
      background: { default: '#F6F4F6', paper: '#FFFFFF' },
      text: {
        primary: '#1D1A1F',
        secondary: '#6C6570',
        disabled: '#9A939E',
        hint: '#9A939E',
      },
      link: BRAND_DARK,
      linkHover: BRAND,
      banner: {
        ...palettes.light.banner,
        info: '#2B6CB0',
        error: '#C62D3A',
        text: '#1D1A1F',
        link: BRAND_DARK,
      },
      errorBackground: '#FDE6E8',
      errorText: '#C62D3A',
      warningBackground: '#FFF2DB',
      warningText: '#B26A00',
      infoBackground: '#E4EEF9',
      infoText: '#2B6CB0',
      navigation: {
        ...palettes.light.navigation,
        background: '#161418',
        indicator: BRAND,
        color: '#D9D4DB',
        selectedColor: '#FFFFFF',
        navItem: { hoverBackground: '#242027' },
        submenu: { background: '#1E1B20' },
      },
    },
  }),
  defaultPageTheme: 'home',
  fontFamily: 'Roboto, sans-serif',
  pageTheme,
  components: components({
    line: '#E7E2E7',
    muted: '#6C6570',
    text: '#1D1A1F',
    surface: '#FFFFFF',
  }),
});

export const koviDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: { main: '#FF4D6D', dark: BRAND, contrastText: '#ffffff' },
      secondary: { main: '#A69FAA' },
      error: { main: '#F06371' },
      warning: { main: '#E6A23C' },
      info: { main: '#6BA5E8' },
      success: { main: '#4CC27D' },
      background: { default: '#121014', paper: '#1B181D' },
      link: '#FF8FA3',
      linkHover: '#FF4D6D',
      navigation: {
        ...palettes.dark.navigation,
        background: '#0C0B0D',
        indicator: '#FF4D6D',
        color: '#D9D4DB',
        selectedColor: '#FFFFFF',
        navItem: { hoverBackground: '#1E1B20' },
        submenu: { background: '#161418' },
      },
    },
  }),
  defaultPageTheme: 'home',
  fontFamily: 'Roboto, sans-serif',
  pageTheme,
  components: components({
    line: '#322D35',
    muted: '#A69FAA',
    text: '#EEEAF0',
    surface: '#1B181D',
  }),
});
